/**
 * diagnosticsEngine.ts — Lightweight real-time code diagnostics.
 * Validates imports, undefined identifiers, and common errors
 * without a full TypeScript compiler. Works in browser + Workers.
 */

import {
  buildSymbolIndex,
  type FileInput,
  type SymbolIndex,
} from './symbolIndex';

// ── Types ────────────────────────────────────────────────────────────

export type DiagnosticSeverity = 'error' | 'warning' | 'info';

export interface Diagnostic {
  fileName: string;
  filePath: string;
  line: number;       // 1-based
  column: number;     // 0-based
  endColumn: number;  // 0-based
  message: string;
  severity: DiagnosticSeverity;
  code: string;       // e.g. 'import-not-found', 'undefined-symbol'
  fix?: { label: string; newText: string; line: number }; // optional auto-fix
}

// ── Known globals that should not be flagged ─────────────────────────

const BUILTIN_GLOBALS = new Set([
  'React', 'Promise', 'Array', 'Object', 'Map', 'Set', 'Date', 'Error',
  'JSON', 'Math', 'String', 'Number', 'Boolean', 'RegExp', 'Symbol',
  'Proxy', 'Reflect', 'Intl', 'URL', 'Event', 'Window', 'Document',
  'HTMLElement', 'SVGElement', 'Response', 'Request', 'Headers',
  'FormData', 'AbortController', 'Navigator', 'Performance', 'Console',
  'Storage', 'Location', 'History', 'setTimeout', 'setInterval',
  'clearTimeout', 'clearInterval', 'fetch', 'console', 'window',
  'document', 'globalThis', 'undefined', 'null', 'NaN', 'Infinity',
  'Buffer', 'process', 'require', 'module', 'exports', '__dirname',
  '__filename', 'TextEncoder', 'TextDecoder', 'Uint8Array', 'Int32Array',
  'Float64Array', 'ArrayBuffer', 'SharedArrayBuffer', 'DataView',
  'WeakMap', 'WeakSet', 'WeakRef', 'FinalizationRegistry',
  'ReadableStream', 'WritableStream', 'TransformStream',
  'crypto', 'atob', 'btoa', 'structuredClone', 'queueMicrotask',
  'requestAnimationFrame', 'cancelAnimationFrame',
  'HTMLInputElement', 'HTMLTextAreaElement', 'HTMLButtonElement',
  'HTMLDivElement', 'HTMLSpanElement', 'HTMLAnchorElement',
  'HTMLFormElement', 'HTMLImageElement', 'HTMLCanvasElement',
  'MouseEvent', 'KeyboardEvent', 'TouchEvent', 'PointerEvent',
  'FocusEvent', 'DragEvent', 'ClipboardEvent', 'CustomEvent',
  'IntersectionObserver', 'MutationObserver', 'ResizeObserver',
  'MediaQueryList', 'Blob', 'File', 'FileReader', 'FileList',
  'URLSearchParams', 'WebSocket', 'EventSource', 'Worker',
  'ServiceWorker', 'BroadcastChannel', 'MessageChannel',
  'AbortSignal', 'Notification', 'Permissions',
  // React types commonly used inline
  'JSX', 'FC', 'ReactNode', 'ReactElement', 'CSSProperties',
  'ChangeEvent', 'FormEvent', 'SyntheticEvent',
  'RefObject', 'MutableRefObject', 'Dispatch', 'SetStateAction',
  'ComponentProps', 'PropsWithChildren', 'HTMLAttributes',
]);

// Known packages whose imports should not be flagged
const KNOWN_PACKAGES = new Set([
  'react', 'react-dom', 'react-dom/client', 'react-router-dom',
  'framer-motion', 'lucide-react', 'sonner', 'clsx', 'tailwind-merge',
  'class-variance-authority', 'zod', 'zustand', 'jotai', 'swr',
  '@tanstack/react-query', 'axios', 'date-fns', 'dayjs', 'lodash',
  'superjson', 'next', 'next/link', 'next/router', 'next/image',
  'next/navigation', '@radix-ui/react-dialog', '@radix-ui/react-popover',
  '@radix-ui/react-tooltip', '@radix-ui/react-dropdown-menu',
  '@radix-ui/react-select', '@radix-ui/react-tabs',
  '@radix-ui/react-accordion', '@radix-ui/react-slot',
  'pixi.js', '@pixi/react', 'three', '@react-three/fiber',
  '@dnd-kit/core', '@dnd-kit/sortable', '@dnd-kit/utilities',
  'recharts', 'chart.js', 'd3', 'mapbox-gl',
]);

// ── Diagnostic checks ────────────────────────────────────────────────

function checkImports(
  fileName: string,
  filePath: string,
  code: string,
  index: SymbolIndex,
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const importRe = /^import\s+(?:type\s+)?(?:[\w$]+\s*,?\s*)?\{([^}]*)\}\s*from\s*["']([^"']+)["']/gm;
  let m: RegExpExecArray | null;

  while ((m = importRe.exec(code)) !== null) {
    const source = m[2];
    const names = m[1];

    // Only check relative imports (local project files)
    if (!source.startsWith('.') && !source.startsWith('@/')) continue;

    // Find the source file in the index
    const sourceFile = resolveImportPath(source, filePath, index);

    if (!sourceFile) {
      const lineInfo = lineColAt(code, m.index);
      diagnostics.push({
        fileName, filePath,
        line: lineInfo.line,
        column: 0,
        endColumn: m[0].length,
        message: `Cannot find module '${source}'`,
        severity: 'error',
        code: 'module-not-found',
      });
      continue;
    }

    // Check each imported name exists in the source file's exports
    for (const part of names.split(',')) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const asMatch = trimmed.match(/(\w+)(?:\s+as\s+\w+)?/);
      if (!asMatch) continue;
      const importedName = asMatch[1];

      const sourceExports = sourceFile.definitions.filter((d) => d.exported);
      const found = sourceExports.some(
        (d) => d.name === importedName || (d.isDefault && importedName === 'default'),
      );

      if (!found && sourceExports.length > 0) {
        const lineInfo = lineColAt(code, m.index);
        const available = sourceExports.map((d) => d.name).join(', ');
        diagnostics.push({
          fileName, filePath,
          line: lineInfo.line,
          column: m[0].indexOf(importedName),
          endColumn: m[0].indexOf(importedName) + importedName.length,
          message: `'${importedName}' is not exported from '${source}'. Available: ${available || 'none'}`,
          severity: 'error',
          code: 'import-not-found',
        });
      }
    }
  }

  return diagnostics;
}

function checkUndefinedIdentifiers(
  fileName: string,
  filePath: string,
  code: string,
  index: SymbolIndex,
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const declared = buildDeclaredSetWithLines(code);
  const lines = code.split('\n');

  // Check JSX component usage
  const jsxRe = /<([A-Z][A-Za-z0-9]+)/g;
  let m: RegExpExecArray | null;

  while ((m = jsxRe.exec(code)) !== null) {
    const name = m[1];
    if (BUILTIN_GLOBALS.has(name)) continue;
    if (declared.has(name)) continue;

    // Check if it's defined in the symbol index (another file)
    const def = index.definitionIndex.get(name);
    if (def && def.exported) continue;

    // Check if it's from a known package import
    const fileSymbols = index.files.get(filePath);
    if (fileSymbols?.imports.some((i) => i.localName === name)) continue;

    const { line, column } = lineColAt(code, m.index + 1); // +1 for <
    diagnostics.push({
      fileName, filePath,
      line,
      column,
      endColumn: column + name.length,
      message: `'${name}' is not defined. Did you forget to import it?`,
      severity: 'warning',
      code: 'undefined-component',
    });
  }

  return diagnostics;
}

function checkDuplicateExports(index: SymbolIndex): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];

  for (const [name, defs] of index.definitions) {
    const exported = defs.filter((d) => d.exported);
    if (exported.length <= 1) continue;

    // Only flag if multiple files export the same name (not same-file overloads)
    const uniqueFiles = new Set(exported.map((d) => d.filePath));
    if (uniqueFiles.size <= 1) continue;

    for (const def of exported) {
      diagnostics.push({
        fileName: def.fileName,
        filePath: def.filePath,
        line: def.line,
        column: def.column,
        endColumn: def.column + name.length,
        message: `'${name}' is also exported from: ${exported
          .filter((d) => d.filePath !== def.filePath)
          .map((d) => d.fileName)
          .join(', ')}`,
        severity: 'info',
        code: 'duplicate-export',
      });
    }
  }

  return diagnostics;
}

// ── Helpers ──────────────────────────────────────────────────────────

function lineColAt(code: string, offset: number): { line: number; column: number } {
  let line = 1;
  let lastNewline = -1;
  for (let i = 0; i < offset && i < code.length; i++) {
    if (code[i] === '\n') { line++; lastNewline = i; }
  }
  return { line, column: offset - lastNewline - 1 };
}

function buildDeclaredSetWithLines(code: string): Set<string> {
  const declared = new Set<string>();
  let m: RegExpExecArray | null;

  // Imports
  const importRe = /import\s+(?:type\s+)?(?:([\w$]+)\s*,?\s*)?\{?\s*([^}'"]*?)\s*\}?\s*from\s*["'][^"']+["']/g;
  while ((m = importRe.exec(code)) !== null) {
    if (m[1]) declared.add(m[1]);
    if (m[2]) {
      for (const part of m[2].split(',')) {
        const asMatch = part.trim().match(/(?:\w+\s+as\s+)?(\w+)$/);
        if (asMatch) declared.add(asMatch[1]);
      }
    }
  }

  // Namespace imports
  const nsRe = /import\s+\*\s+as\s+(\w+)\s+from/g;
  while ((m = nsRe.exec(code)) !== null) declared.add(m[1]);

  // Declarations
  const declRe = /\b(?:const|let|var)\s+(\w+)/g;
  while ((m = declRe.exec(code)) !== null) declared.add(m[1]);

  const funcRe = /\b(?:function|class)\s+(\w+)/g;
  while ((m = funcRe.exec(code)) !== null) declared.add(m[1]);

  // Interfaces, types, enums
  const typeRe = /\b(?:interface|type|enum)\s+(\w+)/g;
  while ((m = typeRe.exec(code)) !== null) declared.add(m[1]);

  return declared;
}

function resolveImportPath(
  importSource: string,
  fromFilePath: string,
  index: SymbolIndex,
): ReturnType<typeof index.files.get> | null {
  // Try exact path match with various extensions
  const extensions = ['', '.ts', '.tsx', '.js', '.jsx', '/index.ts', '/index.tsx', '/index.js'];

  // Resolve relative path
  const fromDir = fromFilePath.replace(/\/[^/]+$/, '');
  let resolved = importSource;
  if (importSource.startsWith('.')) {
    resolved = normalizePath(`${fromDir}/${importSource}`);
  } else if (importSource.startsWith('@/')) {
    resolved = '/' + importSource.slice(2);
  }

  for (const ext of extensions) {
    const candidate = resolved + ext;
    const found = index.files.get(candidate);
    if (found) return found;
  }

  // Try matching by fileName
  const baseName = importSource.split('/').pop() || '';
  for (const [, fileSymbols] of index.files) {
    const fn = fileSymbols.fileName.replace(/\.\w+$/, '');
    if (fn === baseName) return fileSymbols;
  }

  return null;
}

function normalizePath(p: string): string {
  const parts = p.split('/');
  const result: string[] = [];
  for (const part of parts) {
    if (part === '..') result.pop();
    else if (part !== '.' && part !== '') result.push(part);
  }
  return '/' + result.join('/');
}

// ── Main: Run all diagnostics ────────────────────────────────────────

export function runDiagnostics(files: FileInput[]): Diagnostic[] {
  const index = buildSymbolIndex(files);
  const diagnostics: Diagnostic[] = [];

  for (const file of files) {
    if (!file.content) continue;
    const ext = file.fileName.split('.').pop()?.toLowerCase();
    if (!ext || !['tsx', 'ts', 'jsx', 'js'].includes(ext)) continue;

    const filePath = file.filePath || `/${file.fileName}`;
    diagnostics.push(...checkImports(file.fileName, filePath, file.content, index));
    diagnostics.push(...checkUndefinedIdentifiers(file.fileName, filePath, file.content, index));
  }

  diagnostics.push(...checkDuplicateExports(index));

  return diagnostics;
}

/** Run diagnostics for a single file using an existing index */
export function runFileDiagnostics(
  file: FileInput,
  index: SymbolIndex,
): Diagnostic[] {
  if (!file.content) return [];
  const filePath = file.filePath || `/${file.fileName}`;
  const diagnostics: Diagnostic[] = [];

  diagnostics.push(...checkImports(file.fileName, filePath, file.content, index));
  diagnostics.push(...checkUndefinedIdentifiers(file.fileName, filePath, file.content, index));

  return diagnostics;
}
